'use client';

import { useRecurly } from '@recurly/react-recurly';
import { PayPalInstance } from '@recurly/recurly-js/lib/paypal';
import { FormEvent, JSX, useCallback, useEffect, useState } from 'react';
import { BillingInfo } from 'subscription-service-client';

import { Button } from '@videoblocks/design-system';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import { PayPalError, PayPalSuccessData, PayPalToken } from '../../../(recaptcha)/sign-up/checkout/PayPalCheckout';
import { PayPalService } from '../../../(recaptcha)/sign-up/checkout/PayPalService';
import { getBraintreeClientAuthorization } from '../../../(recaptcha)/sign-up/checkout/actions';
import { updateBillingInfo } from './actions';
import BillingInfoCountryInput from './billingInfoCountryInput';
import BillingInfoTextInput from './billingInfoTextInput';

const logger = LoggerFactory.create('PayPal Billing Info');

export default function PaypalInfoForm({ billingInfo }: { billingInfo?: BillingInfo }): JSX.Element {
    const [error, setError] = useState<string>();
    const paypalConfig = PayPalService.getConfig();
    const recurly = useRecurly();
    const [paypalInstance, setPaypalInstance] = useState<PayPalInstance | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [clientAuthorization, setClientAuthorization] = useState<string | undefined>(undefined);

    const handlePaypalSuccess = (data: PayPalSuccessData) => {
        updateBillingInfo({ tokenId: data.tokenId }).then(
            (result) => {
                if (!result.success) {
                    setError(result.error);
                }
            },
            (error) => {
                setError(
                    'An error occurred while updating your billing information. Please try again later. If the issue persists, please contact support.',
                );
                logger.error({ error: error });
            },
        );
    };

    const initializePayPal = useCallback(() => {
        if (isInitialized) return;

        try {
            const paypal = PayPalService.createPayPalInstance(recurly, clientAuthorization);

            paypal.on('error', (err: PayPalError) => {
                setIsLoading(false);
                logger.error({ error: err }, 'An error occurred with Paypal while updating payment info');
                setError('An error occurred with PayPal');
            });

            paypal.on('token', (token: PayPalToken) => {
                setIsLoading(false);

                const successData: PayPalSuccessData = {
                    tokenId: token.id,
                };

                if (paypalConfig.useMockPayPal) {
                    logger.info('Mock PayPal: Success - Token generated');
                    return;
                }

                handlePaypalSuccess(successData);
            });

            paypal.on('cancel', () => {
                setIsLoading(false);
                setError('PayPal payment was cancelled');
            });

            setPaypalInstance(paypal);
            setIsInitialized(true);
        } catch {
            setError('Failed to initialize PayPal');
        }
    }, [isInitialized, paypalConfig.useMockPayPal, recurly, clientAuthorization, error, setError, handlePaypalSuccess]);

    // Fetch client authorization when component mounts (only if not using mock)
    useEffect(() => {
        if (!paypalConfig.useMockPayPal && !clientAuthorization) {
            getBraintreeClientAuthorization()
                .then(setClientAuthorization)
                .catch(() => {
                    setError('Failed to initialize PayPal. Please try again.');
                });
        }
    }, [paypalConfig.useMockPayPal, clientAuthorization, error, setError]);

    useEffect(() => {
        if (paypalConfig.useMockPayPal || (recurly && clientAuthorization && !isInitialized)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            initializePayPal();
        }
    }, [initializePayPal, paypalConfig.useMockPayPal, recurly, clientAuthorization, isInitialized]);

    const handlePayPalClick = (event: FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!paypalInstance || isLoading) return;

        setIsLoading(true);

        try {
            paypalInstance.start();
        } catch {
            setIsLoading(false);
            setError(
                'There has been an error processing your payment. Retry your request or try a different payment method. Contact customer support at support@storyblocks.com for more help.',
            );
        }
    };

    return (
        <form onSubmit={handlePayPalClick} className="flex flex-col w-full">
            <div className="py-4 border-b-1 border-gray-300">
                <img src="/assets/images/paypal-wordmark.svg" alt="PayPal" />
            </div>
            <Button variant="outlined-dark" role="button" disabled={isLoading || !isInitialized} className="w-fit py-4">
                Change Paypal Account
            </Button>
            {error && <span className="text-red-600">{error}</span>}
            <BillingInfoTextInput
                label="First Name"
                placeholder={billingInfo?.firstName ?? ''}
                name="firstName"
                isEditable={false}
            />
            <BillingInfoTextInput
                label="Last Name"
                placeholder={billingInfo?.lastName ?? ''}
                name="lastName"
                isEditable={false}
            />
            {billingInfo?.postalCode && (
                <BillingInfoTextInput
                    label="Zip/Postal Code"
                    placeholder={billingInfo?.postalCode ?? ''}
                    name="postalCode"
                    isEditable={false}
                />
            )}
            {billingInfo?.country && (
                <BillingInfoCountryInput initialCountry={billingInfo?.country ?? ''} isEditable={false} />
            )}
        </form>
    );
}
